<template>
  <section
    class="flex flex-col"
    :class="[
      isMyOwn ? 'ml-px h-full w-[360px] min-w-[360px] max-w-[360px] shrink-0 border-l border-l-bor-nav tablet:w-[300px] tablet:min-w-[300px] tablet:max-w-[300px]' : 'w-full',
      embedInScrollParent ? 'min-h-0' : 'grow min-h-0 overflow-hidden',
    ]"
  >
    <header
      v-if="isMyOwn"
      class="flex shrink-0 items-center gap-1 p-2"
      data-test="chat-gallery-own-header"
    >
      <wui-btn
        icon
        variant="neut"
        state="text"
        :size="40"
        class="shrink-0 rounded-10!"
        prepend-icon="closeRegularMdM"
        data-test="chat-gallery-close"
        @click="emit('close')"
      >
        <span class="sr-only select-none">
          {{ $t('common.close') }}
        </span>
      </wui-btn>
      <h4 class="min-w-0 grow px-2 text-heading-sb-18-24 text-fg-base-fg3">
        {{ $t('common.savedMessages') }}
      </h4>
    </header>
    <div
      v-if="showStickyChrome"
      ref="stickyChromeRef"
      class="sticky top-0 z-10 bg-bg-base-bg1 mt-2.5"
      data-test="chat-gallery-sticky-chrome"
    >
      <div
        v-if="showAttachmentTabs"
        class="px-4 py-[6px]"
        data-test="chat-gallery-tabs"
      >
        <div
          ref="tabsContainerRef"
          class="attachment-tabs-scroll flex items-center gap-1 overflow-x-auto snap-x snap-mandatory"
          :class="isDraggingTabs ? 'cursor-grabbing select-none' : 'cursor-grab'"
          @mousedown="onTabsMouseDown"
          @dragstart.prevent
        >
          <button
            v-for="tab in galleryTabs"
            :key="tab.type"
            :ref="(element) => setAttachmentTabRef(tab.type, element)"
            class="rounded-8 shrink-0 snap-start flex items-center gap-1 p-[6px] cursor-pointer text-body-r-15-22 transition-colors"
            :class="[
              fileTypeForView === tab.type
                ? 'bg-surf-bg-6 text-surf-brand-base-def'
                : 'text-surf-fg-quin hover:bg-surf-bg-6 hover:text-surf-fg-sec',
            ]"
            :data-test="`chat-gallery-tab-${tab.type}`"
            @click="openFilesView(tab.type)"
          >
            {{ tab.label }}
          </button>
        </div>
      </div>

      <div
        v-if="showGallerySearch"
        class="shrink-0 px-4 pb-2"
        data-test="chat-gallery-search"
      >
        <wui-input
          v-model="gallerySearch"
          :placeholder="gallerySearchPlaceholder"
          with-clear
          prepend-icon="searchM"
        />
      </div>

      <div
        v-if="showLoadMoreBar"
        class="flex flex-col items-center gap-1 px-4 pb-2"
      >
        <wui-btn
          v-if="canLoadMoreHistory || hasPendingFiles"
          variant="neut"
          state="alpha"
          :disabled="isGalleryLoading"
          data-test="chat-gallery-load-more"
          @click="onLoadMoreClick"
        >
          {{ $t('common.chatGalleryLoadEarlier') }}
        </wui-btn>
        <p
          v-else-if="isGalleryLoading"
          class="text-body-r-13-18 text-surf-fg-quin"
        >
          {{ $t('common.loading') }}...
        </p>
      </div>

      <div
        v-if="showStickyDateHeader"
        class="flex items-center gap-3 px-6 pb-1 pt-0.5"
        data-test="chat-gallery-sticky-date"
      >
        <p class="min-w-0 grow py-1 text-body-m-14-20 text-fg-base-fg17">
          {{ stickyDateLabel }}
        </p>
        <label
          v-if="!isMyOwn"
          class="flex shrink-0 items-center gap-1.5 py-1"
          data-test="chat-gallery-only-mine"
        >
          <span class="text-body-r-14-20 text-fg-base-fg3">
            {{ $t('common.chatGalleryOnlyMine') }}
          </span>
          <wui-input-switch
            v-model="onlyMine"
            small
          />
        </label>
      </div>
    </div>
    <div
      class="relative flex flex-col px-4 pb-4"
      :class="embedInScrollParent ? undefined : 'grow min-h-0'"
      data-test="chat-gallery-content"
      @touchstart.passive="onGalleryContentTouchStart"
      @touchend.passive="onGalleryContentTouchEnd"
      @touchcancel.passive="onGalleryContentTouchCancel"
    >
      <chat-gallery-participants
        v-if="showParticipantsTab && room"
        v-show="fileTypeForView === GalleryTypes.PARTICIPANTS"
        :room="room"
        :embed-in-scroll-parent="embedInScrollParent"
      />
      <div
        v-if="isAttachmentsEnabled"
        v-show="fileTypeForView !== GalleryTypes.PARTICIPANTS"
        ref="attachmentsScrollRef"
        data-test="chat-gallery-attachments-scroll"
        class="flex flex-col"
        :class="embedInScrollParent ? undefined : 'grow min-h-0 overflow-y-auto pr-1 [scrollbar-gutter:stable]'"
      >
        <template v-if="groupedAttachments.length">
          <section
            v-for="(group, groupIndex) in groupedAttachments"
            :key="group.key"
            :ref="(element) => setGroupSectionRef(group.key, group.label, element)"
            class="flex flex-col gap-1"
            :data-gallery-group="group.key"
          >
            <p
              v-if="groupIndex > 0"
              class="px-2 py-1 text-body-m-14-20 text-fg-base-fg17"
              data-test="chat-gallery-group-date"
            >
              {{ group.label }}
            </p>
            <div :class="isPhotoGridView ? 'grid grid-cols-3 gap-1' : 'flex flex-col gap-1'">
              <template v-if="isPhotoGridView">
                <div
                  v-for="item in group.items"
                  :key="item.id"
                  v-touch.long="() => onMediaLongPress(item)"
                  class="group relative aspect-square overflow-hidden rounded-4 bg-surf-neut-soft-def hover:bg-surf-neut-soft-hov active:bg-surf-neut-soft-pres"
                  :style="{ 'anchor-name': getMediaAnchorName(item.id) }"
                  data-test="chat-gallery-media-item"
                  @click="onMediaClick(item)"
                  @contextmenu.prevent="onMediaContextMenu(item)"
                >
                  <img
                    v-if="item.file"
                    :src="getAuthenticatedMediaUrl(`/files/${item.file.filecookie}`)"
                    :alt="item.file.filename"
                    class="pointer-events-none h-full w-full object-cover transition-transform duration-200 group-hover:scale-105"
                  >
                  <button
                    v-if="!isMobileLayout"
                    type="button"
                    class="absolute top-1 right-1 z-10 flex size-[28px] items-center justify-center rounded-4 bg-black/70 text-white opacity-0 transition-opacity group-hover:opacity-100"
                    :class="{ 'opacity-100': activeMediaItem?.id === item.id && isMediaMenuShowed }"
                    data-test="chat-gallery-media-menu"
                    :aria-label="$t('common.more')"
                    @click.stop="openMediaMenu(item)"
                  >
                    <wui-icon
                      name="kebabVerticalM"
                      class="size-4!"
                    />
                  </button>
                </div>
              </template>
              <template v-else-if="isVideosView">
                <chat-gallery-video-item
                  v-for="item in group.items"
                  :key="item.id"
                  :title="getVideoTitle(item)"
                  :sender-name="getVideoSenderName(item)"
                  :meta-label="getVideoMeta(item)"
                  :preview-src="getVideoPreviewSrc(item)"
                  :duration-seconds="getVideoDurationSeconds(item)"
                  :is-event-recording="isEventRecordingVideo(item)"
                  :show-kebab="!isMobileLayout"
                  :is-menu-active="activeMediaItem?.id === item.id && isMediaMenuShowed"
                  :anchor-name="getMediaAnchorName(item.id)"
                  @open="onMediaClick(item)"
                  @open-menu="openMediaMenu(item)"
                  @contextmenu="onMediaContextMenu(item)"
                  @long-press="onMediaLongPress(item)"
                />
              </template>
              <template v-else-if="isLinksView">
                <div
                  v-for="item in group.items"
                  :key="item.id"
                  v-touch.long="() => onMediaLongPress(item)"
                  class="group relative flex cursor-pointer items-start gap-2 rounded-8 px-2 py-1.5 hover:bg-surf-neut-soft-hov active:bg-surf-neut-soft-pres"
                  :style="{ 'anchor-name': getMediaAnchorName(item.id) }"
                  data-test="chat-gallery-link-item"
                  @click="onMediaClick(item)"
                  @contextmenu.prevent="onMediaContextMenu(item)"
                >
                  <div class="min-w-0 flex-1 py-0.5">
                    <p class="truncate text-body-r-14-20 text-surf-brand-base-def">
                      {{ getLinkTitle(item.content ?? '') }}
                    </p>
                    <p class="truncate text-label-r-12-18 text-fg-base-fg17">
                      {{ getLinkMeta(item) }}
                    </p>
                  </div>
                  <button
                    v-if="!isMobileLayout"
                    type="button"
                    class="absolute top-1 right-1 z-10 flex size-6 items-center justify-center rounded-4 text-fg-base-fg17 opacity-0 transition-opacity group-hover:opacity-100 hover:bg-surf-neut-soft-hov"
                    :class="{ 'opacity-100': activeMediaItem?.id === item.id && isMediaMenuShowed }"
                    data-test="chat-gallery-link-menu"
                    :aria-label="$t('common.more')"
                    @click.stop="openMediaMenu(item)"
                  >
                    <wui-icon
                      name="kebabVerticalM"
                      class="size-4!"
                    />
                  </button>
                </div>
              </template>
              <template v-else-if="isFilesView">
                <div
                  v-for="item in group.items"
                  :key="item.id"
                  v-touch.long="() => onMediaLongPress(item)"
                  class="group relative flex cursor-pointer items-center gap-2 rounded-8 px-2 py-1.5 hover:bg-surf-neut-soft-hov active:bg-surf-neut-soft-pres"
                  :style="{ 'anchor-name': getMediaAnchorName(item.id) }"
                  data-test="chat-gallery-file-item"
                  @click="onMediaClick(item)"
                  @contextmenu.prevent="onMediaContextMenu(item)"
                >
                  <div class="pointer-events-none size-10 shrink-0">
                    <file-icon
                      v-if="item.file"
                      :mime="item.file.filemime ?? ''"
                      :cookie="item.file.filecookie"
                      :name="item.file.filename"
                      :open-preview-on-click="false"
                    />
                  </div>
                  <div class="min-w-0 flex-1 py-0.5">
                    <p
                      class="truncate text-label-m-13-18 text-fg-base-fg3"
                      :title="item.file?.filename"
                    >
                      {{ item.file?.filename }}
                    </p>
                    <p
                      v-if="item.file"
                      class="truncate text-label-r-12-18 text-fg-base-fg17"
                    >
                      {{ getFileSubtitle(item) }}
                    </p>
                  </div>
                  <button
                    v-if="!isMobileLayout"
                    type="button"
                    class="absolute top-1 right-1 z-10 flex size-6 items-center justify-center rounded-4 text-fg-base-fg17 opacity-0 transition-opacity group-hover:opacity-100 hover:bg-surf-neut-soft-hov"
                    :class="{ 'opacity-100': activeMediaItem?.id === item.id && isMediaMenuShowed }"
                    data-test="chat-gallery-file-menu"
                    :aria-label="$t('common.more')"
                    @click.stop="openMediaMenu(item)"
                  >
                    <wui-icon
                      name="kebabVerticalM"
                      class="size-4!"
                    />
                  </button>
                </div>
              </template>
              <template v-else>
                <button
                  v-for="item in group.items"
                  :key="item.id"
                  class="w-full rounded-10 bg-surf-neut-soft-def hover:bg-surf-neut-soft-hov active:bg-surf-neut-soft-pres p-2 text-left"
                  @click="onAttachmentClick(item)"
                >
                  <div class="flex items-center gap-2">
                    <div class="min-w-0">
                      <p
                        v-if="item.file"
                        class="truncate text-body-r-14-20 text-fg-base-fg3"
                      >
                        {{ item.file.filename }}
                      </p>
                    </div>
                  </div>
                </button>
              </template>
            </div>
          </section>
        </template>

        <p
          v-else-if="isFilteredEmpty"
          class="px-3 py-8 text-center text-body-r-15-22 text-fg-base-fg17"
          data-test="chat-gallery-filtered-empty"
        >
          {{ $t('common.nothingFound') }}
        </p>
        <p
          v-else-if="isFullEmpty"
          class="px-3 py-16 text-center text-body-r-15-22 text-fg-base-fg17"
          data-test="chat-gallery-empty"
        >
          {{ emptyStateText }}
        </p>

        <div
          ref="loadMoreSentinelRef"
          class="h-1 w-full shrink-0"
          aria-hidden="true"
          data-test="chat-gallery-load-more-sentinel"
        />
      </div>

      <div
        v-if="isMediaMenuShowed && activeMediaItem"
        v-click-outside="hideMediaMenu"
        class="absolute z-20 w-max"
        :style="mediaMenuStyle"
        data-test="chat-gallery-media-context-menu"
      >
        <wui-card
          class="min-w-[240px]"
          state="text"
          shadow="overflow"
        >
          <wui-list>
            <template v-if="activeMediaItem && !isEventRecordingVideo(activeMediaItem)">
              <wui-list-item
                icon="messageArrowRightM"
                data-test="chat-gallery-media-go-to-message"
                @click="onMediaGoToMessage"
              >
                <p>{{ $t('common.goToMessage') }}</p>
              </wui-list-item>
              <wui-list-item
                icon="forwardRight"
                data-test="chat-gallery-media-forward"
                @click="onMediaForward"
              >
                <p>{{ mediaForwardLabel }}</p>
              </wui-list-item>
            </template>
            <wui-list-item
              v-if="isActiveLinkItem"
              icon="linkM"
              data-test="chat-gallery-media-copy-link"
              @click="onMediaCopyLink"
            >
              <p>{{ $t('common.chatGalleryCopyLink') }}</p>
            </wui-list-item>
            <wui-list-item
              v-else
              icon="downloadM"
              data-test="chat-gallery-media-download"
              @click="onMediaDownload"
            >
              <p>{{ $t('common.download') }}</p>
            </wui-list-item>
            <template v-if="canDeleteActiveMedia">
              <hr class="mx-1 my-1 border-t-bor-nav">
              <wui-list-item
                icon="trashM"
                variant="negative"
                data-test="chat-gallery-media-delete"
                @click="onMediaDelete"
              >
                <p>{{ $t('common.remove') }}</p>
              </wui-list-item>
            </template>
          </wui-list>
        </wui-card>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import {
  ClickOutside as vClickOutside,
  useDialog,
  useRenderNotifier,
  WuiBtn,
  WuiCard,
  WuiIcon,
  WuiInput,
  WuiInputSwitch,
  WuiList,
  WuiListItem,
} from '@wui/common-library'
import {
  HistoryMessageItem,
  IMFile,
  isOutgoingMessage,
  isTextMessage,
  MessageCallReceived,
  MessageCallRecordCompleted,
  MessageIncomingEvent,
  MessageOutgoingEvent,
} from '@wui/im'
import { unix } from 'dayjs'
import { ComponentPublicInstance, computed, nextTick, onBeforeUnmount, onMounted, ref, toRef, watch } from 'vue'


import { ChatForwardMessage } from '@/widgets/chat-forward-message-widget'
import {
  isImageAttachment,
  isVideoAttachment,
  isAudioAttachment,
  useOpenAttachmentViewer,
} from '@/widgets/media-with-thread-chat'

import { FileIcon } from '@/entities/chat-event-file'
import { useStoreContacts } from '@/entities/contact'
import { usePinEventsStore } from '@/entities/messages'
import { useRoom } from '@/entities/use-room'

import { useConfirmDialog } from '@/shared/components/dialogs'
import { useCurrentUser } from '@/shared/composables/current-user'
import { useLocalization } from '@/shared/composables/localization'
import { isMobileLayout } from '@/shared/composables/use-mobile'
import { vTouch } from '@/shared/directives/v-touch'
import { AttachmentItem, GalleryTypes } from '@/shared/types/chat-aside'
import { Room } from '@/shared/types/store'
import { bytesConverter } from '@/shared/utils/bytes-converter'
import { download as downloadAttachment } from '@/shared/utils/download-files'
import sort from '@/shared/utils/sort'
import { getMessageLinkMarkup } from '@/shared/utils/text-entities'
import { getAuthenticatedMediaUrl, getPathNameFromURL } from '@/shared/utils/url'

import { CHAT_GALLERY_ATTACHMENTS_ENABLED } from '../config'
import { groupGalleryItemsByDate, useChatGalleryHistory } from '../lib'

import ChatGalleryParticipants from './chat-gallery-participants.vue'
import ChatGalleryVideoItem from './chat-gallery-video-item.vue'

const isAttachmentsEnabled = CHAT_GALLERY_ATTACHMENTS_ENABLED

const emit = defineEmits<{
  (event: 'close'): void
  (event: 'go-to-message', eventId: number): void
}>()

const props = defineProps<{
  pServed: string,
  room?: Room,
  isMyOwn?: boolean,
  embedInScrollParent?: boolean,
}>()

const { t } = useLocalization()
const { openAttachmentViewer } = useOpenAttachmentViewer()
const { showDialog } = useDialog()
const { show: showConfirm } = useConfirmDialog()
const { notifyError, notifySuccess } = useRenderNotifier()
const roomApi = useRoom()
const { getPinEventsForRoom } = usePinEventsStore()
const { findByPServed } = useStoreContacts()
const { currentUser } = useCurrentUser()

const {
  allMessages,
  canLoadMoreHistory,
  hasMoreHistory,
  hasPendingFiles,
  isLoading: isGalleryLoading,
  loadOlder,
} = useChatGalleryHistory(toRef(props, 'pServed'))

const onlyMine = ref(false)
const linksSearch = ref('')
const videosSearch = ref('')
const filesSearch = ref('')
const textMessages = computed(() => allMessages.value.filter(isTextMessage))
const callRecordMessages = computed(() => {
  return allMessages.value.filter((message): message is HistoryMessageItem<MessageCallRecordCompleted> => {
    return (message.item as MessageCallReceived)?.event === 'call_record_completed'
  })
})

const isEventRecordingVideo = (item: AttachmentItem) => item.content === 'event-recording'

const getVideoTitle = (item: AttachmentItem) => {
  if (isEventRecordingVideo(item)) {
    return item.file?.filename || t('calls.callRecord')
  }

  return item.file?.filename || t('common.videos')
}

const getVideoSenderName = (item: AttachmentItem) => {
  if (isEventRecordingVideo(item)) {
    return ''
  }

  if (isOutgoingMessage(item.event)) {
    return currentUser.value?.name || t('common.noName')
  }

  const executor = 'executor' in item.event.item ? item.event.item.executor : undefined
  if (!executor) {
    return t('common.noName')
  }

  return findByPServed(executor)?.name || t('common.noName')
}

const getVideoMeta = (item: AttachmentItem) => {
  const date = unix(item.event.timestamp).tz().format('D MMMM YYYY')
  const size = typeof item.file?.filesize === 'number'
    ? String(bytesConverter(item.file.filesize))
    : ''

  return [date, size].filter(Boolean).join(' • ')
}

const getVideoPreviewSrc = (item: AttachmentItem) => {
  if (!item.file?.filecookie) {
    return ''
  }

  const cookie = item.file.filecookie
  const base = cookie.startsWith('http') || cookie.startsWith('/')
    ? getAuthenticatedMediaUrl(cookie.startsWith('http') ? getPathNameFromURL(cookie) : cookie)
    : getAuthenticatedMediaUrl(`/files/${cookie}`)

  return `${base}#t=0.001`
}

const getVideoDurationSeconds = (item: AttachmentItem) => {
  if (!isEventRecordingVideo(item)) {
    return 0
  }

  const duration = (item.event.item as unknown as MessageCallRecordCompleted).duration
  return typeof duration === 'number' && duration > 0 ? duration : 0
}

const getCallRecordFileCookie = (url: string) => {
  const pathName = url.includes('://') ? getPathNameFromURL(url) : url
  return pathName.replace(/^\/files\//, '').replace(/^\//, '')
}
const showParticipantsTab = computed(() => Boolean(props.room?.isGroup))

const galleryTabs = computed(() => {
  const tabs: { type: GalleryTypes, label: string }[] = []
  if (showParticipantsTab.value) {
    tabs.push({ type: GalleryTypes.PARTICIPANTS, label: t('common.participants') })
  }
  if (isAttachmentsEnabled) {
    tabs.push(
      { type: GalleryTypes.PHOTO, label: t('common.images') },
      { type: GalleryTypes.VIDEO, label: t('common.videos') },
      { type: GalleryTypes.FILE, label: t('common.files') },
      { type: GalleryTypes.LINK, label: t('common.links') },
    )
  }
  return tabs
})

const showAttachmentTabs = computed(() => galleryTabs.value.length > 0)

const tabsForView = computed(() => galleryTabs.value.map((tab) => tab.type))

const showLoadMoreBar = computed(() => {
  if (!isAttachmentsEnabled || fileTypeForView.value === GalleryTypes.PARTICIPANTS) {
    return false
  }

  return hasMoreHistory.value || isGalleryLoading.value || hasPendingFiles.value
})

const attachmentItemsByType = computed(() => {
  const grouped = new Map<GalleryTypes, AttachmentItem[]>([
    [GalleryTypes.PHOTO, []],
    [GalleryTypes.VIDEO, []],
    [GalleryTypes.FILE, []],
    [GalleryTypes.LINK, []],
  ])

  const sortedMessages = textMessages.value.slice().sort(sort.chatEvents.byTimestamp)
  sortedMessages.forEach((message) => {
    message.item.files?.forEach((file, index) => {
      const type = getFileCategory(file)
      grouped.get(type)?.push({
        id: `${message.eventId}-${file.filecookie}-${index}`,
        event: message as HistoryMessageItem<MessageOutgoingEvent | MessageIncomingEvent>,
        file,
      })
    })

    const links = getMessageLinkMarkup(message.item)
    links.forEach((link, index) => {
      grouped.get(GalleryTypes.LINK)?.push({
        id: `${message.eventId}-link-${index}`,
        event: message as HistoryMessageItem<MessageOutgoingEvent | MessageIncomingEvent>,
        content: link,
      })
    })
  })

  callRecordMessages.value
    .slice()
    .sort(sort.chatEvents.byTimestamp)
    .forEach((message) => {
      const url = message.item.callRecordUrl ?? ''
      if (!url) {
        return
      }

      const filename = url.split('/').pop()?.split('?')[0] || t('calls.callRecord')
      grouped.get(GalleryTypes.VIDEO)?.push({
        id: `${message.eventId}-call-record`,
        event: message as unknown as HistoryMessageItem<MessageOutgoingEvent | MessageIncomingEvent>,
        file: {
          filecookie: getCallRecordFileCookie(url),
          filename,
          filemime: filename.toLowerCase().endsWith('.mp3') ? 'audio/mpeg' : 'video/mp4',
        },
        content: 'event-recording',
      })
    })

  return grouped
})

const attachmentsForActiveType = computed(() => {
  return attachmentItemsByType.value.get(fileTypeForView.value) ?? []
})

const searchedAttachments = computed(() => {
  const items = attachmentsForActiveType.value

  if (fileTypeForView.value === GalleryTypes.LINK) {
    const query = linksSearch.value.trim().toLowerCase()
    if (!query) {
      return items
    }

    return items.filter((item) => {
      const title = getLinkTitle(item.content ?? '').toLowerCase()
      return title.includes(query)
    })
  }

  if (fileTypeForView.value === GalleryTypes.VIDEO) {
    const query = videosSearch.value.trim().toLowerCase()
    if (!query) {
      return items
    }

    return items.filter((item) => {
      const title = getVideoTitle(item).toLowerCase()
      const sender = getVideoSenderName(item).toLowerCase()
      return title.includes(query) || sender.includes(query)
    })
  }

  if (fileTypeForView.value === GalleryTypes.FILE) {
    const query = filesSearch.value.trim().toLowerCase()
    if (!query) {
      return items
    }

    return items.filter((item) => {
      const title = (item.file?.filename ?? '').toLowerCase()
      const sender = getLinkSenderName(item.event).toLowerCase()
      return title.includes(query) || sender.includes(query)
    })
  }

  return items
})

const activeAttachments = computed(() => {
  if (!onlyMine.value) {
    return searchedAttachments.value
  }

  return searchedAttachments.value.filter((item) => {
    if (isEventRecordingVideo(item)) {
      return false
    }

    return isOutgoingMessage(item.event)
  })
})

const groupedAttachments = computed(() => {
  return groupGalleryItemsByDate(
    activeAttachments.value.map((item) => ({
      ...item,
      timestamp: item.event.timestamp,
    })),
  )
})

const isPhotoGridView = computed(() => fileTypeForView.value === GalleryTypes.PHOTO)

const isVideosView = computed(() => fileTypeForView.value === GalleryTypes.VIDEO)

const isLinksView = computed(() => fileTypeForView.value === GalleryTypes.LINK)

const isFilesView = computed(() => fileTypeForView.value === GalleryTypes.FILE)

const showGallerySearch = computed(() => {
  return isLinksView.value || isVideosView.value || isFilesView.value
})

const gallerySearch = computed({
  get: () => {
    if (isLinksView.value) {
      return linksSearch.value
    }
    if (isVideosView.value) {
      return videosSearch.value
    }
    if (isFilesView.value) {
      return filesSearch.value
    }
    return ''
  },
  set: (value: string) => {
    if (isLinksView.value) {
      linksSearch.value = value
      return
    }
    if (isVideosView.value) {
      videosSearch.value = value
      return
    }
    if (isFilesView.value) {
      filesSearch.value = value
    }
  },
})

const gallerySearchPlaceholder = computed(() => {
  if (isVideosView.value) {
    return t('common.chatGallerySearchVideos')
  }
  if (isFilesView.value) {
    return t('common.chatGallerySearchFiles')
  }
  return t('common.chatGallerySearchLinks')
})

const isFullEmpty = computed(() => {
  return fileTypeForView.value !== GalleryTypes.PARTICIPANTS
    && attachmentsForActiveType.value.length === 0
    && !linksSearch.value.trim()
    && !videosSearch.value.trim()
    && !filesSearch.value.trim()
})

const isFilteredEmpty = computed(() => {
  return !isFullEmpty.value
    && fileTypeForView.value !== GalleryTypes.PARTICIPANTS
    && groupedAttachments.value.length === 0
})

const emptyStateText = computed(() => {
  switch (fileTypeForView.value) {
  case GalleryTypes.PHOTO:
    return t('common.chatGalleryNoImages')
  case GalleryTypes.VIDEO:
    return t('common.chatGalleryNoVideos')
  case GalleryTypes.FILE:
    return t('common.chatGalleryNoFiles')
  case GalleryTypes.LINK:
    return t('common.chatGalleryNoLinks')
  default:
    return t('common.nothingFound')
  }
})

const showStickyDateHeader = computed(() => {
  return isAttachmentsEnabled
    && fileTypeForView.value !== GalleryTypes.PARTICIPANTS
    && groupedAttachments.value.length > 0
})

const showStickyChrome = computed(() => {
  return showAttachmentTabs.value
    || showGallerySearch.value
    || showStickyDateHeader.value
    || showLoadMoreBar.value
})

const attachmentTabRefs = ref(new Map<GalleryTypes, HTMLButtonElement>())
const tabsContainerRef = ref<HTMLElement | null>(null)
const attachmentsScrollRef = ref<HTMLElement | null>(null)
const loadMoreSentinelRef = ref<HTMLElement | null>(null)
const stickyChromeRef = ref<HTMLElement | null>(null)
const stickyDateLabel = ref('')
const groupSectionByKey = new Map<string, { el: HTMLElement, label: string }>()
const isMediaMenuShowed = ref(false)
const activeMediaItem = ref<AttachmentItem | null>(null)
const suppressMediaClick = ref(false)
const isDraggingTabs = ref(false)
const preventTabClickAfterDrag = ref(false)
const isTabsMouseDown = ref(false)
const tabsLastClientX = ref(0)
const tabsDragDistance = ref(0)
let loadMoreObserver: IntersectionObserver | null = null
let detachStickyDateScroll: (() => void) | null = null

const isActiveLinkItem = computed(() => {
  const item = activeMediaItem.value
  return Boolean(item?.content && item.content !== 'event-recording' && !item.file)
})

const canDeleteActiveMedia = computed(() => {
  if (!activeMediaItem.value) {
    return false
  }

  return isOutgoingMessage(activeMediaItem.value.event) && !isEventRecordingVideo(activeMediaItem.value)
})

const setGroupSectionRef = (key: string, label: string, element: Element | ComponentPublicInstance | null) => {
  const el = element instanceof HTMLElement
    ? element
    : element && '$el' in element && element.$el instanceof HTMLElement
      ? element.$el
      : null

  if (el) {
    groupSectionByKey.set(key, { el, label })
    return
  }

  groupSectionByKey.delete(key)
}

const updateStickyDateLabel = () => {
  const groups = groupedAttachments.value
  if (!groups.length) {
    stickyDateLabel.value = ''
    return
  }

  const stickyBottom = stickyChromeRef.value?.getBoundingClientRect().bottom ?? 0
  let currentLabel = groups[0]!.label

  for (const group of groups) {
    const entry = groupSectionByKey.get(group.key)
    if (!entry) {
      continue
    }

    if (entry.el.getBoundingClientRect().top <= stickyBottom + 4) {
      currentLabel = group.label
    }
  }

  stickyDateLabel.value = currentLabel
}

const getGalleryScrollRoot = (): HTMLElement | null => {
  if (props.embedInScrollParent) {
    return findScrollParent(stickyChromeRef.value)
      ?? findScrollParent(attachmentsScrollRef.value)
  }

  return attachmentsScrollRef.value
}

const setupStickyDateScroll = () => {
  detachStickyDateScroll?.()
  detachStickyDateScroll = null

  if (!showStickyDateHeader.value) {
    stickyDateLabel.value = ''
    return
  }

  stickyDateLabel.value = groupedAttachments.value[0]?.label ?? ''

  const root = getGalleryScrollRoot()
  if (!root) {
    return
  }

  const onScroll = () => updateStickyDateLabel()
  root.addEventListener('scroll', onScroll, { passive: true })
  detachStickyDateScroll = () => root.removeEventListener('scroll', onScroll)
  void nextTick(updateStickyDateLabel)
}

const getMediaAnchorName = (id: string) => {
  return `--gallery-media-${String(id).replace(/[^a-zA-Z0-9_-]/g, '-')}`
}

const mediaMenuStyle = computed(() => {
  if (!activeMediaItem.value) {
    return {}
  }

  return {
    'position-anchor': getMediaAnchorName(activeMediaItem.value.id),
    'position-area': 'center',
    'position-try-fallbacks': 'flip-block, flip-inline',
  } as Record<string, string>
})

const mediaForwardLabel = computed(() => {
  if (fileTypeForView.value === GalleryTypes.VIDEO) {
    return t('common.chatGalleryForwardVideo')
  }

  if (fileTypeForView.value === GalleryTypes.FILE) {
    return t('common.chatGalleryForwardFile')
  }

  if (fileTypeForView.value === GalleryTypes.LINK) {
    return t('common.chatGalleryForwardLink')
  }

  return t('common.chatGalleryForwardImage')
})

const hideMediaMenu = () => {
  isMediaMenuShowed.value = false
}

const openMediaMenu = (item: AttachmentItem, options?: { suppressClick?: boolean }) => {
  activeMediaItem.value = item
  if (options?.suppressClick) {
    suppressMediaClick.value = true
  }
  setTimeout(() => {
    isMediaMenuShowed.value = true
  }, 0)
}

const onMediaClick = (item: AttachmentItem) => {
  if (suppressMediaClick.value) {
    suppressMediaClick.value = false
    return
  }

  void onAttachmentClick(item)
}

const onMediaContextMenu = (item: AttachmentItem) => {
  if (isMobileLayout.value) {
    return
  }

  openMediaMenu(item, { suppressClick: true })
}

const onMediaLongPress = (item: AttachmentItem) => {
  openMediaMenu(item, { suppressClick: true })
}

const onMediaGoToMessage = () => {
  if (!activeMediaItem.value) {
    return
  }

  onGoToMessage(activeMediaItem.value)
  hideMediaMenu()
}

const onMediaForward = () => {
  if (!activeMediaItem.value) {
    return
  }

  onForwardAttachment(activeMediaItem.value)
  hideMediaMenu()
}

const onMediaDownload = () => {
  if (!activeMediaItem.value?.file) {
    return
  }

  downloadAttachment(activeMediaItem.value.file, '/files/')
  hideMediaMenu()
}

const onMediaCopyLink = () => {
  if (!activeMediaItem.value) {
    return
  }

  void onCopyLink(activeMediaItem.value)
  hideMediaMenu()
}

const onMediaDelete = () => {
  if (!activeMediaItem.value) {
    return
  }

  const item = activeMediaItem.value
  hideMediaMenu()
  void onDeleteAttachment(item)
}

const setAttachmentTabRef = (type: GalleryTypes, element: Element | ComponentPublicInstance | null) => {
  if (element instanceof HTMLButtonElement) {
    attachmentTabRefs.value.set(type, element)
    return
  }

  attachmentTabRefs.value.delete(type)
}

const openFilesView = (type: GalleryTypes) => {
  if (preventTabClickAfterDrag.value) {
    return
  }

  fileTypeForView.value = type
}

const CONTENT_SWIPE_THRESHOLD_PX = 60
const CONTENT_SWIPE_HORIZONTAL_RATIO = 1.2

let contentSwipeStartX = 0
let contentSwipeStartY = 0
let isContentSwipeTracking = false

const switchToAdjacentTab = (direction: 1 | -1) => {
  const tabs = tabsForView.value
  const currentIndex = tabs.indexOf(fileTypeForView.value)
  if (currentIndex < 0) {
    return
  }

  const nextTab = tabs[currentIndex + direction]
  if (nextTab === undefined) {
    return
  }

  fileTypeForView.value = nextTab
}

const onGalleryContentTouchStart = (event: TouchEvent) => {
  if (!isMobileLayout.value || event.touches.length !== 1) {
    isContentSwipeTracking = false
    return
  }

  const touch = event.touches[0]
  if (!touch) {
    return
  }

  contentSwipeStartX = touch.clientX
  contentSwipeStartY = touch.clientY
  isContentSwipeTracking = true
}

const onGalleryContentTouchCancel = () => {
  isContentSwipeTracking = false
}

const onGalleryContentTouchEnd = (event: TouchEvent) => {
  if (!isContentSwipeTracking || !isMobileLayout.value) {
    return
  }

  isContentSwipeTracking = false

  if (isMediaMenuShowed.value) {
    return
  }

  const touch = event.changedTouches[0]
  if (!touch) {
    return
  }

  const deltaX = touch.clientX - contentSwipeStartX
  const deltaY = touch.clientY - contentSwipeStartY
  const absDeltaX = Math.abs(deltaX)
  const absDeltaY = Math.abs(deltaY)

  if (absDeltaX < CONTENT_SWIPE_THRESHOLD_PX) {
    return
  }

  if (absDeltaX < absDeltaY * CONTENT_SWIPE_HORIZONTAL_RATIO) {
    return
  }

  // Swipe left → next tab, swipe right → previous tab
  switchToAdjacentTab(deltaX < 0 ? 1 : -1)
}

const onTabsMouseDown = (event: MouseEvent) => {
  if (event.button !== 0 || !tabsContainerRef.value) {
    return
  }

  isTabsMouseDown.value = true
  isDraggingTabs.value = false
  tabsDragDistance.value = 0
  tabsLastClientX.value = event.clientX
  window.addEventListener('mousemove', onTabsMouseMove)
  window.addEventListener('mouseup', onTabsMouseUp)
}

const onTabsMouseMove = (event: MouseEvent) => {
  if (!isTabsMouseDown.value || !tabsContainerRef.value) {
    return
  }

  const delta = event.clientX - tabsLastClientX.value
  tabsLastClientX.value = event.clientX
  tabsDragDistance.value += Math.abs(delta)
  if (tabsDragDistance.value < 5) {
    return
  }

  isDraggingTabs.value = true
  if (tabsContainerRef.value.style.scrollSnapType !== 'none') {
    tabsContainerRef.value.style.scrollSnapType = 'none'
  }
  tabsContainerRef.value.scrollLeft -= delta
  event.preventDefault()
}

const finishTabsDragging = () => {
  if (!tabsContainerRef.value) {
    isTabsMouseDown.value = false
    isDraggingTabs.value = false
    return
  }

  isTabsMouseDown.value = false
  isDraggingTabs.value = false
  tabsContainerRef.value.style.removeProperty('scroll-snap-type')

  if (tabsDragDistance.value >= 5) {
    requestAnimationFrame(() => {
      if (tabsContainerRef.value) {
        tabsContainerRef.value.scrollTo({
          left: tabsContainerRef.value.scrollLeft,
          behavior: 'smooth',
        })
      }
    })

    preventTabClickAfterDrag.value = true
    setTimeout(() => {
      preventTabClickAfterDrag.value = false
    }, 0)
  }
  window.removeEventListener('mousemove', onTabsMouseMove)
  window.removeEventListener('mouseup', onTabsMouseUp)
}

const onTabsMouseUp = () => {
  finishTabsDragging()
}

const onAttachmentClick = async (item: AttachmentItem) => {
  if (fileTypeForView.value === GalleryTypes.LINK && item.content && item.content !== 'event-recording') {
    const href = getLinkHref(item.content)
    if (href) {
      window.open(href, '_blank', 'noopener,noreferrer')
    }
    return
  }

  if (!item.file) {
    return
  }

  if (isAudioAttachment(item.file)) {
    window.open(getAuthenticatedMediaUrl(`/files/${item.file.filecookie}`), '_blank', 'noopener,noreferrer')
    return
  }

  await openAttachmentViewer(item.file, item.event)
}

const getLinkTitle = (link: string) => {
  const href = getLinkHref(link)
  if (href) {
    return href
  }
  return link.replace(/<[^>]*>/g, '').trim()
}

const getLinkHref = (link: string) => {
  const match = link.match(/href="([^"]+)"/i)
  return match?.[1] ?? null
}

const getLinkSenderName = (event: AttachmentItem['event']) => {
  if (isOutgoingMessage(event)) {
    return currentUser.value?.name || t('common.noName')
  }

  const executor = 'executor' in event.item ? event.item.executor : undefined
  if (!executor) {
    return t('common.noName')
  }

  return findByPServed(executor)?.name || t('common.noName')
}

const getLinkMeta = (item: AttachmentItem) => {
  const name = getLinkSenderName(item.event)
  const date = unix(item.event.timestamp).tz().format('D MMMM YYYY')
  return `${name}, ${date}`
}

const getFileSubtitle = (item: AttachmentItem) => {
  const name = getLinkSenderName(item.event)
  const date = unix(item.event.timestamp).tz().format('D MMMM YYYY')
  const size = typeof item.file?.filesize === 'number'
    ? String(bytesConverter(item.file.filesize))
    : ''
  const meta = [date, size].filter(Boolean).join(' • ')

  return `${name}, ${meta}`
}

const onGoToMessage = (item: AttachmentItem) => {
  emit('go-to-message', item.event.eventId)
}

const onForwardAttachment = (item: AttachmentItem) => {
  showDialog(ChatForwardMessage, {
    chatEvents: [item.event],
    hasOverlay: true,
  })
}

const onCopyLink = async (item: AttachmentItem) => {
  const href = getLinkHref(item.content ?? '')
  if (!href) {
    return
  }

  try {
    await navigator.clipboard.writeText(href)
    notifySuccess(t('common.linkCopiedToClipboard'))
  } catch {
    notifyError(t('common.unknownError'))
  }
}

const onDeleteAttachment = async (item: AttachmentItem) => {
  const confirmed = await showConfirm({
    title: t('common.remove'),
    message: t('common.areYouSureYouWantToDelete'),
    confirmLabel: t('common.remove'),
  })
  if (!confirmed) {
    return
  }

  try {
    const deleted = await roomApi.delete(item.event.eventId, props.pServed)
    if (!deleted) {
      return
    }

    const pinEvents = getPinEventsForRoom(props.pServed)
    const pinEvent = pinEvents?.find((pin) => pin.eventId === item.event.eventId)
    if (pinEvent) {
      await roomApi.deletePin(pinEvent.eventId, props.pServed)
    }
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : t('common.unknownError')
    notifyError(errorMessage)
  }
}

const getFileCategory = (file: IMFile) => {
  if (isImageAttachment(file)) {
    return GalleryTypes.PHOTO
  }

  if (isVideoAttachment(file)) {
    return GalleryTypes.VIDEO
  }

  // Audio, documents and other files share the Files tab
  return GalleryTypes.FILE
}

const fileTypeForView = ref(GalleryTypes.PARTICIPANTS)

const scrollToActiveTab = () => {
  const activeTab = attachmentTabRefs.value.get(fileTypeForView.value)
  if (!activeTab) {
    return
  }

  activeTab.scrollIntoView({
    behavior: 'smooth',
    block: 'nearest',
    inline: 'center',
  })
}

const findScrollParent = (el: HTMLElement | null) => {
  let node = el?.parentElement ?? null
  while (node) {
    const { overflowY } = getComputedStyle(node)
    if (overflowY === 'auto' || overflowY === 'scroll') {
      return node
    }
    node = node.parentElement
  }
  return null
}

let isSentinelIntersecting = false

const onLoadMoreClick = () => {
  // One batch per click; mark sentinel as already seen so IO does not immediately re-fire.
  isSentinelIntersecting = true
  void loadOlder()
}

const setupLoadMoreObserver = () => {
  loadMoreObserver?.disconnect()
  loadMoreObserver = null

  if (!isAttachmentsEnabled || !loadMoreSentinelRef.value) {
    return
  }

  if (fileTypeForView.value === GalleryTypes.PARTICIPANTS) {
    return
  }

  // Empty tabs keep the button only — sentinel would always intersect and exhaust history.
  if (!groupedAttachments.value.length) {
    return
  }

  if (!hasMoreHistory.value) {
    return
  }

  const root = props.embedInScrollParent
    ? findScrollParent(loadMoreSentinelRef.value)
    : attachmentsScrollRef.value

  loadMoreObserver = new IntersectionObserver(
    (entries) => {
      const isIntersecting = entries.some(({ isIntersecting: visible }) => visible)
      const becameVisible = isIntersecting && !isSentinelIntersecting
      isSentinelIntersecting = isIntersecting

      if (!becameVisible || isGalleryLoading.value || !hasMoreHistory.value) {
        return
      }

      void loadOlder()
    },
    {
      root,
      rootMargin: '240px 0px',
      threshold: 0,
    },
  )
  loadMoreObserver.observe(loadMoreSentinelRef.value)
}

watch(tabsForView, () => {
  if (!tabsForView.value.length) {
    return
  }

  if (!tabsForView.value.includes(fileTypeForView.value)) {
    fileTypeForView.value = tabsForView.value[0]!
  }
}, { immediate: true })

watch(fileTypeForView, (type) => {
  if (type !== GalleryTypes.LINK) {
    linksSearch.value = ''
  }
  if (type !== GalleryTypes.VIDEO) {
    videosSearch.value = ''
  }
  if (type !== GalleryTypes.FILE) {
    filesSearch.value = ''
  }
  hideMediaMenu()
  activeMediaItem.value = null
  isSentinelIntersecting = false
  void nextTick(() => {
    scrollToActiveTab()
    setupLoadMoreObserver()
  })
}, { immediate: true })

watch(
  [loadMoreSentinelRef, attachmentsScrollRef, hasMoreHistory, () => groupedAttachments.value.length > 0],
  () => {
    void nextTick(setupLoadMoreObserver)
  },
  { immediate: true },
)

watch([showStickyDateHeader, groupedAttachments, stickyChromeRef, attachmentsScrollRef], () => {
  void nextTick(setupStickyDateScroll)
}, { immediate: true })

onMounted(() => {
  void nextTick(() => {
    setupLoadMoreObserver()
    setupStickyDateScroll()
  })
})

onBeforeUnmount(() => {
  window.removeEventListener('mousemove', onTabsMouseMove)
  window.removeEventListener('mouseup', onTabsMouseUp)
  loadMoreObserver?.disconnect()
  loadMoreObserver = null
  detachStickyDateScroll?.()
  detachStickyDateScroll = null
})
</script>

<style scoped>
.attachment-tabs-scroll {
  -ms-overflow-style: none;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;
  touch-action: pan-x;
  overscroll-behavior-x: contain;
}

.attachment-tabs-scroll::-webkit-scrollbar {
  display: none;
}
</style>
